// React
import * as React from 'react';

// Redux
import { connect } from 'react-redux';

// Router
import { Router as Router, Redirect, Route } from 'react-router-dom';
import createBrowserHistory from 'history/createBrowserHistory';

// MainStore
import { MainStore } from './stores/MainStore';

// MuiTheme
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';

// Components
import Fade from '@material-ui/core/Fade';
import Icon from '@material-ui/core/Icon';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Navigation from './components/Navigation';
import AppBarTop from './components/AppBarTop';

// Pickers
import MuiPickersUtilsProvider from 'material-ui-pickers/utils/MuiPickersUtilsProvider';
import DateFnsUtils from 'material-ui-pickers/utils/date-fns-utils';
import * as frCH from 'date-fns/locale/fr-CH';

// Views
import Login from './views/Login';
import Home from './views/Home';
import Result from './views/Result';
import ResultProfile from './views/ResultProfile';
import Rdv from './views/Rdv';
import RdvProfile from './views/RdvProfile';
import Conseils from './views/Conseils';
import Breed from './views/Breed';
import BreedProfile from './views/BreedProfile';
import BreedProfileImage from './views/BreedProfileImage';
import Breeder from './views/Breeder';
import BreederProfile from './views/BreederProfile';
import Animal from './views/Animal';
import AnimalProfile from './views/AnimalProfile';
import Gallery from './views/Gallery';
import GalleryProfile from './views/GalleryProfile';
import Colonie from './views/Colonie';
import Article from './views/Article';
import ArticleProfile from './views/ArticleProfile';
import Comite from './views/Comite';
import ComiteProfile from './views/ComiteProfile';
import Texts from './views/Texts';
import TextsProfile from './views/TextsProfile';
import Notification from './components/Notification';

// Import React FilePond
import { registerPlugin } from "react-filepond";

// Import FilePond styles
import "filepond/dist/filepond.min.css";

import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImageCrop from 'filepond-plugin-image-crop';
import FilePondPluginImageResize from 'filepond-plugin-image-resize';
import FilePondPluginImageTransform from 'filepond-plugin-image-transform';

// Register the plugin
registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImageCrop, FilePondPluginImageResize, FilePondPluginImageTransform);

// Functions
import { getURLByEnv } from './framework/utils/URL';
import { NetworkManager } from './managers/NetworkManager';

interface State {
  open: boolean;
}

interface Props {
  navigation: any;
  notification: any;
  overlay: any;
  user: any;
  history: any;
}

const theme = createMuiTheme({
  palette: {
    primary: {
      contrastText: '#fff',
      dark: '#0D47A1',
      light: '#90CAF9',
      main: '#1976D2'
    },
    secondary: {
      contrastText: '#000',
      dark: '#ba000d',
      light: '#ff7961',
      main: '#f44336'
    }
  }
});

const history = createBrowserHistory();

const PrivateRoute = ({ component: Component, isConnected, ...rest }: any) => (
  <Route {...rest} render={(props) => (
    isConnected
      ? <Component {...props} />
      : <Redirect to={`${getURLByEnv()}/Connexion`} />
  )} />
);

/**
 * @description React App
 * @author Lucas Fridez <lucas@fridez.dev>
 * @export
 * @class App
 * @extends {React.Component}
 */
export default connect((state) => state)(class App extends React.Component<Props, State> {

  /**
   * Creates an instance of App.
   * @author Lucas Fridez <lucas@fridez.dev>
   * @param {*} props
   * @memberof App
   */
  constructor(props: any) {
    super(props);
  }

  componentWillMount() {
    if (NetworkManager.getUserCredentials() !== undefined) {
        MainStore.loginUser(NetworkManager.getUserCredentials());
    }
}

  /**
   * @description Render the WebApp in the Browser
   * @author Lucas Fridez <lucas@fridez.dev>
   * @returns
   * @memberof App
   */
  public render() {
    return (
      <Router history={history}>
        <MuiThemeProvider theme={theme}>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frCH}>
            <div className="App">
              <Navigation />
              <div className={this.props.navigation.navigation ? 'container-reduced' : 'container'}>
                <AppBarTop />
                {this.props.navigation.backShown && <span className="back-btn mouse-hover"
                  onClick={() => {
                    MainStore.goBack(history.location.pathname);
                    history.goBack();
                  }}>
                  <Icon>keyboard_backspace</Icon>
                  <Typography variant="subheading">Retour</Typography>
                </span>}
                <div role="main">
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/`} component={Home} />
                  <Route exact={true} path={`${getURLByEnv()}/Connexion`} component={Login} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Galeries`} component={Gallery} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Galeries/Créer`} component={GalleryProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Galeries/Modifier/:id`} component={GalleryProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Comite`} component={Comite} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Comite/Créer`} component={ComiteProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Comite/Modifier/:id`} component={ComiteProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Animaux`} component={Animal} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Animaux/Créer`} component={AnimalProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Animaux/Modifier/:id`} component={AnimalProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Races`} component={Breed} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Races/Créer`} component={BreedProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Races/Modifier/:id`} component={BreedProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Races/Images/:id`} component={BreedProfileImage} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Eleveurs`} component={Breeder} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Eleveurs/Créer`} component={BreederProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Eleveurs/Modifier/:id`} component={BreederProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Textes`} component={Texts} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Textes/Créer`} component={TextsProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Textes/Modifier/:id`} component={TextsProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Resultats`} component={Result} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Resultats/Créer`} component={ResultProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Resultats/Modifier/:id`} component={ResultProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/RendezVous`} component={Rdv} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/RendezVous/Créer`} component={RdvProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/RendezVous/Modifier/:id`} component={RdvProfile} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Conseils`} component={Conseils} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Colonie`} component={Colonie} />

                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Articles`} component={Article} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Articles/Créer`} component={ArticleProfile} />
                  <PrivateRoute isConnected={this.props.user.connected} exact={true} path={`${getURLByEnv()}/Articles/Modifier/:id`} component={ArticleProfile} />
                </div>
              </div>
              {this.props.overlay.overlay.show &&
                <Fade in={true}>
                  <div id="overlay">
                    <CircularProgress color="primary" thickness={3} size={75} />
                    <Typography color="primary" variant="body1">{this.props.overlay.overlay.text}</Typography>
                  </div>
                </Fade>
              }
              <Notification
                className={this.props.notification.notification.className}
                open={this.props.notification.notification.show}
                text={this.props.notification.notification.text} />
            </div>
          </MuiPickersUtilsProvider>
        </MuiThemeProvider>
      </Router>
    );
  }
});



// WEBPACK FOOTER //
// ./src/app/App.tsx